import React, {useState} from 'react';
import {useLocalization} from 'packages/localization/localization';
// models
import {Product} from 'packages/products/models';
import {FilterMarkers} from 'packages/filter/models';
// api
import AddProductToFavorite from 'packages/products/API/AddProductToFavorite';
import RemoveProductFromFavorite from 'packages/products/API/RemoveProductFromFavorite';
// components
import DraftEditorReact from 'packages/draftjs/DraftEditor.react';
import Picture from 'packages/files/Picture.react';
import CategoryIcon from 'packages/filter/components/CategoryItemIconSimple.react';
import Tooltip from 'packages/tooltip/Tootip.react';
// icons
import {ReactComponent as PointIcon} from 'src/icons/place.svg';
import {ReactComponent as LikeIcon} from 'src/icons/like.svg';
import {ReactComponent as TimeIcon} from 'src/icons/builder/time.svg';
import {ReactComponent as StartTimeIcon} from 'src/icons/time.svg';
import {ReactComponent as DistanceIcon} from 'src/icons/map/route-outline.svg';
import {ReactComponent as LevelsIcon} from 'src/icons/levels.svg';
// styles
import {CreateSheet, GetColor, css} from 'packages/aphrodite/aphrodite';
import {Dark} from 'src/themes/colors';
import {PrimaryDraft} from 'src/themes/draftjs';
import {MakeButton, PrimaryFilled} from 'src/themes/button';
import {PrintDuration} from 'packages/helpers/Duration';
import {PrintDistance} from 'packages/session/utils/PrintDistance';
import {OpenLink, OpenPage} from 'packages/history/history';
import {DarkTooltip} from 'src/themes/tooltip';
import IsIFrame from 'packages/helpers/IsIFrame';
import PrintMoney from 'packages/helpers/PrintMoney';
import 'locale/namespaces/product/en.json';
import {MediaObject} from 'packages/media/utils/GetMediaByTypes';

interface Props {
    product: Product;
}

export default function ProductCard({product}: Props) {
    const {t} = useLocalization('product');
    const [isLiked, setIsLiked] = useState<boolean>(product.is_liked);

    const toggleFavorite = async (e: React.MouseEvent) => {
        e.stopPropagation();
        !isLiked ? await AddProductToFavorite(product.id) : await RemoveProductFromFavorite(product.id);
        setIsLiked(!isLiked);
    };

    const media = new MediaObject(product.media);

    return (
        <div
            className={css(Styles.item)}
            onClick={OpenPage(`tours/${product.uri}`)}
        >
            <div className={css(Styles.headerLine, Styles.headerLineMobile)}>
                <h3 className={css(Styles.itemLabel)}>{product.title}</h3>
                {!IsIFrame() && (
                    <LikeIcon
                        className={css(Styles.likeIcon, isLiked && Styles.likeIconActive)}
                        onClick={toggleFavorite}
                        width="24"
                        height="24"
                    />
                )}
            </div>
            <div className={css(Styles.itemLeft)}>
                {media.photos && media.photos.length && media.photos[0].file ? (
                    <Picture
                        {...{
                            image: media.photos[0].file,
                            styles: {
                                root: Styles.img,
                            },
                            thumb: true,
                        }}
                    />
                ) : null}
                <ul className={css(Styles.details)}>
                    {product.start_times.length ? (
                        <li className={css(Styles.detailsItem)}>
                            <div className={css(Styles.detailsLabel)}>
                                <StartTimeIcon
                                    className={css(Styles.detailsIcon)}
                                    width="14"
                                    height="14"
                                />
                                <span>{t('time')}</span>
                            </div>
                            <div className={css(Styles.detailsValue)}>{product.start_times.join(' / ')}</div>
                        </li>
                    ) : null}
                    {product.duration > 0 ? (
                        <li className={css(Styles.detailsItem)}>
                            <div className={css(Styles.detailsLabel)}>
                                <TimeIcon
                                    className={css(Styles.detailsIcon)}
                                    width="14"
                                    height="14"
                                />
                                <span>{t('duration')}</span>
                            </div>
                            <div className={css(Styles.detailsValue)}>{PrintDuration(product.duration, 'daymin')}</div>
                        </li>
                    ) : null}
                    {product.distance > 0 ? (
                        <li className={css(Styles.detailsItem)}>
                            <div className={css(Styles.detailsLabel)}>
                                <DistanceIcon
                                    className={css(Styles.detailsIcon)}
                                    width="14"
                                    height="14"
                                />
                                <span>{t('distance')}</span>
                            </div>
                            <div className={css(Styles.detailsValue)}>{PrintDistance(product.distance, 'approx')}</div>
                        </li>
                    ) : null}
                    {product.filter_settings?.[FilterMarkers.difficulty]?.[0] ? (
                        <li className={css(Styles.detailsItem)}>
                            <div className={css(Styles.detailsLabel)}>
                                <LevelsIcon
                                    className={css(Styles.detailsIcon)}
                                    width="14"
                                    height="14"
                                />
                                <span>{t('level')}</span>
                            </div>
                            <div className={css(Styles.detailsValue)}>
                                {product.filter_settings[FilterMarkers.difficulty][0].title}
                            </div>
                        </li>
                    ) : null}
                </ul>
            </div>
            <div className={css(Styles.itemContent)}>
                <div className={css(Styles.headerLine)}>
                    <h3 className={css(Styles.itemLabel)}>{product.title}</h3>
                    {!IsIFrame() && (
                        <LikeIcon
                            className={css(Styles.likeIcon, isLiked && Styles.likeIconActive)}
                            onClick={toggleFavorite}
                            width="24"
                            height="24"
                        />
                    )}
                </div>
                <div className={css(Styles.headerLine)}>
                    <div className={css(Styles.price)}>
                        <span>{PrintMoney(product.price, product.currency.code)}</span>
                    </div>
                    {product.filter_settings[FilterMarkers.vehicle] ? (
                        <ul className={css(Styles.direction)}>
                            {product.filter_settings?.[FilterMarkers.vehicle]
                                ? product.filter_settings[FilterMarkers.vehicle]?.map(vehicle => {
                                      if (!vehicle.icon) return null;
                                      return (
                                          <li key={vehicle.guid}>
                                              <Tooltip
                                                  {...{
                                                      theme: DarkTooltip,
                                                      content: vehicle.title,
                                                  }}
                                              >
                                                  <span>
                                                      <CategoryIcon
                                                          className={css(Styles.directionIcon)}
                                                          icon={vehicle.icon}
                                                          width="18"
                                                          height="18"
                                                      />
                                                  </span>
                                              </Tooltip>
                                          </li>
                                      );
                                  })
                                : null}
                        </ul>
                    ) : null}
                </div>
                {product.filter_settings[FilterMarkers.starting_point]?.[0] ? (
                    <div className={css(Styles.geo)}>
                        <div className={css(Styles.startPoint)}>
                            <PointIcon
                                className={css(Styles.startPointIcon)}
                                width="16"
                                height="16"
                            />
                            <span>{t('point')}</span>
                        </div>
                        <div className={css(Styles.geoPosition)}>
                            {product.filter_settings[FilterMarkers.starting_point][0].title}
                        </div>
                    </div>
                ) : null}
                <ul className={css(Styles.categories)}>
                    {product.filter_settings?.[FilterMarkers.type]?.map(type => (
                        <li
                            key={type.id}
                            className={css(Styles.category)}
                        >
                            {type.title}
                        </li>
                    ))}
                </ul>
                <DraftEditorReact
                    {...{
                        readOnly: true,
                        defaultValue: product.short_description,
                        theme: PrimaryDraft,
                        classNames: {
                            root: Styles.draftRoot,
                        },
                    }}
                />

                {!IsIFrame() && product.tour_operator_name.length > 0 && product.tour_operator_uri.length > 0 && (
                    <div className={css(Styles.tourOperator)}>
                        <div className={css(Styles.tourOperatorLabel)}>Tour operator</div>
                        <a
                            onClick={OpenLink}
                            className={css(Styles.tourOperatorLink)}
                            href={`/tour-operators/${product.tour_operator_uri}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {product.tour_operator_name}
                        </a>
                    </div>
                )}

                <div className={css(Styles.footer, !product.author && Styles.footerSingle)}>
                    {product.author ? (
                        <div className={css(Styles.creator)}>
                            <small className={css(Styles.creatorLabel)}>{t('createdBy')}</small>
                            <a
                                className={css(Styles.creatorLink)}
                                href={`/authors/${product.author.guid}`}
                                target="_blank"
                                rel="noreferrer"
                                onClick={OpenLink}
                            >
                                {product.author.full_name}
                            </a>
                        </div>
                    ) : null}
                    <div className={css(Styles.nav)}>
                        <button
                            {...{
                                type: 'button',
                                className: css(MakeButton(PrimaryFilled)),
                                onClick: e => OpenPage(`tours/${product.uri}`)(e),
                            }}
                        >
                            {t('learn')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

const Styles = CreateSheet({
    item: {
        display: 'flex',
        alignItems: 'flex-start',
        padding: 20,
        color: Dark.default,
        background: GetColor('elementBg'),
        boxShadow: `0px 10px 40px -10px ${GetColor('shadowPrimaryColor')}`,
        border: `1px solid ${GetColor('strokeColor')}`,
        borderRadius: 25,
        cursor: 'pointer',
        '@media(max-width: 640px)': {
            display: 'block',
            maxWidth: 400,
            margin: '0 auto',
        },
    },
    draftRoot: {
        marginTop: 15,
        overflow: 'hidden',
        ':nth-child(1n) .DraftEditor-root': {
            fontSize: '1rem',
            fontWeight: 600,
            border: 'none',
            padding: 0,
            minHeight: 0,
        },
    },
    img: {
        width: '100%',
        margin: '0 0 20px 0',
        borderRadius: 12,
    },
    details: {
        margin: 0,
        padding: 0,
        fontSize: '0.857rem',
        fontWeight: 600,
        listStyle: 'none',
    },
    detailsItem: {
        display: 'flex',
        margin: '5px 0 0 0',
        justifyContent: 'space-between',
    },
    detailsLabel: {
        display: 'flex',
        alignItems: 'center',
        color: GetColor('primary'),
    },
    detailsValue: {
        color: GetColor('accentDark'),
    },
    detailsIcon: {
        display: 'block',
        marginRight: 10,
        fill: GetColor('primary'),
    },
    itemLabel: {
        margin: 0,
        fontSize: '1.428rem',
    },
    itemContent: {
        marginLeft: 45,
        flexGrow: 1,
        '@media(max-width: 640px)': {
            marginTop: 20,
            marginLeft: 0,
        },
    },
    headerLine: {
        display: 'flex',
        marginBottom: 10,
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        '@media(max-width: 640px)': {
            display: 'none',
        },
    },
    headerLineMobile: {
        display: 'none',
        '@media(max-width: 640px)': {
            display: 'flex',
        },
    },
    itemLeft: {
        width: 235,
        minWidth: 235,
        flexGrow: 0,
        '@media(max-width: 640px)': {
            width: '100%',
        },
    },
    price: {
        display: 'flex',
        fontSize: '1.285rem',
        fontWeight: 600,
        alignItems: 'center',
    },
    geo: {
        display: 'flex',
        fontSize: '0.857rem',
        fontWeight: 600,
        alignItems: 'center',
    },
    startPoint: {
        display: 'flex',
        alignItems: 'center',
        marginRight: 10,
    },
    startPointIcon: {
        display: 'block',
        marginRight: 10,
        fill: GetColor('primary'),
    },
    geoPosition: {
        color: GetColor('accentDark'),
    },
    likeIcon: {
        display: 'block',
        cursor: 'pointer',
        ':nth-child(1n) path': {
            stroke: GetColor('accent'),
        },
    },
    likeIconActive: {
        fill: GetColor('accent'),
    },
    direction: {
        display: 'flex',
        margin: '0 -7.5px',
        padding: 0,
        listStyle: 'none',
    },
    directionIcon: {
        display: 'block',
        margin: '0 7.5px',
        ':nth-child(1n) path': {
            fill: GetColor('secondary'),
        },
    },
    categories: {
        display: 'flex',
        margin: '15px -5px 5px -5px',
        padding: 0,
        flexWrap: 'wrap',
        listStyle: 'none',
    },
    category: {
        margin: 5,
        padding: '5px 10px',
        fontSize: '0.785rem',
        fontWeight: 600,
        color: GetColor('secondary'),
        background: GetColor('blockBg'),
        borderRadius: 5,
        whiteSpace: 'nowrap',
    },
    tourOperator: {
        margin: '15px 0 0 0',
        display: 'flex',
        gap: 15,
        alignItems: 'center',
        fontSize: '0.8571rem',
        fontWeight: 600,
        lineHeight: 1,
    },
    tourOperatorLabel: {
        color: GetColor('secondary'),
    },
    tourOperatorLink: {
        fontWeight: 700,
        color: GetColor('primary'),
        textDecoration: 'none',
        ':hover': {
            textDecoration: 'underline',
        },
    },
    footer: {
        display: 'flex',
        margin: '15px 0 0 0',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    footerSingle: {
        justifyContent: 'flex-end',
    },
    creator: {
        marginRight: 20,
        fontSize: '0.857rem',
        fontWeight: 700,
    },
    creatorLabel: {
        display: 'block',
        marginTop: 5,
        color: GetColor('primary'),
    },
    creatorLink: {
        color: GetColor('accent'),
        textDecoration: 'none',
        fontSize: '1rem',
        ':hover': {
            textDecoration: 'underline',
        },
    },
    nav: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
});
